import flatten from "lodash/flatten";
import { useInfiniteQuery } from "react-query";
import { useWorkflowsApi } from "@/react/api";
import { useSupportUnreleasedWorkflowsFeaturesEnabled } from "@circle-react/components/shared/Workflows/hooks/useSupportUnreleasedWorkflowsFeaturesEnabled";
import type { Params } from "@circle-react/helpers/urlHelpers/toQueryString";
import { useAreAiWorkflowTemplatesEnabled } from "../../../hooks/useAreAiWorkflowTemplatesEnabled";

const PER_PAGE = 100;

export const useTemplates = (category?: string) => {
  const hasUnreleasedWorkflows = useSupportUnreleasedWorkflowsFeaturesEnabled();
  const areAiWorkflowTemplatesEnabled = useAreAiWorkflowTemplatesEnabled();
  const workflowsApi = useWorkflowsApi();

  const fetchPage = (page: number, category?: string) => {
    const queryParams: Params = {
      page,
      per_page: PER_PAGE,
      v2_2_enabled: true,
    };

    if (category && category !== "all") {
      queryParams.category = category;
    }

    return workflowsApi.templates({ params: queryParams });
  };

  const { data, isLoading, fetchNextPage } = useInfiniteQuery<any, Error>(
    ["workflow-templates-list", category],
    ({ pageParam = 1 }: { pageParam?: number }) =>
      fetchPage(pageParam, category),
    {
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const pages = data?.pages || [];
  const allTemplates = flatten(pages.map(page => page.records));

  let availableTemplates = hasUnreleasedWorkflows
    ? allTemplates
    : allTemplates.filter(template => template.released);

  if (!areAiWorkflowTemplatesEnabled) {
    availableTemplates = availableTemplates.filter(
      template => !template.categories?.includes("ai_workflows"),
    );
  }

  return {
    templates: availableTemplates,
    totalCount: pages[0]?.total_count || 0,
    isLoading,
    fetchNextPage,
  };
};
